import React, { useEffect, useState, useCallback, useRef } from 'react'
import { connect } from 'react-redux'

import Captions, { CaptionsManager } from '../Captions'

import type { CuestomCue } from '../Captions'
import { HTML5PlayerProps } from './types'

function HTML5Player(
  props: HTML5PlayerProps,
  playerRef: React.ForwardedRef<HTMLVideoElement>
) {
  const internalRef = useRef<HTMLVideoElement | null>(null)
  const [captions, setCaptions] = useState([])

  const handlePlaying = useCallback(() => {
    !!props.onPlaying && props.onPlaying()
  }, [props.onPlaying])

  const handlePaused = useCallback(() => {
    !!props.onPaused && props.onPaused()
  }, [props.onPaused])

  const handleError = useCallback(
    (error) => {
      console.error(error)
      !!props.onError && props.onError(error)
    },
    [props.onError]
  )

  const handleTimeUpdate = useCallback(
    (event: Event) => {
      !!props.onTimeUpdate &&
        props.onTimeUpdate((event.target as HTMLVideoElement)?.currentTime)
    },
    [props.onTimeUpdate]
  )

  const handleEnded = useCallback(() => {
    !!props.onEnded && props.onEnded()
  }, [props.onEnded])

  const handleLoadedData = useCallback(
    (event: Event) => {
      !!props.onLoadedData &&
        props.onLoadedData((event.target as HTMLVideoElement)?.duration)
      const textTracks = internalRef.current?.textTracks
      if (textTracks) {
        for (let i = 0; i < textTracks.length; i++) {
          textTracks[i].mode = 'disabled'
        }
        for (let i = 0; i < (textTracks?.length ?? 0); i++) {
          const track = textTracks[i]
          if (track instanceof TextTrack) {
            if (track.kind === 'captions' || track.kind === 'subtitles') {
              track.mode = 'showing'
              break
            }
          }
        }
      }
    },
    [props.onLoadedData]
  )

  const handleSeeked = useCallback(() => {
    !!props.onSeeked && props.onSeeked()
  }, [props.onSeeked])

  useEffect(() => {
    const captionsManager = new CaptionsManager(internalRef.current)
    captionsManager
      .onEnter(setCaptions)
      .onExit((cueId: number) => {
        setCaptions((prevCaptions) =>
          prevCaptions.filter((c: CuestomCue) => c.id !== cueId)
        )
      })
      .init()
    if (internalRef.current !== null) {
      internalRef.current.addEventListener('playing', handlePlaying)
      internalRef.current.addEventListener('timeupdate', handleTimeUpdate)
      internalRef.current.addEventListener('pause', handlePaused)
      internalRef.current.addEventListener('error', handleError)
      internalRef.current.addEventListener('ended', handleEnded)
      internalRef.current.addEventListener('loadeddata', handleLoadedData)
      internalRef.current.addEventListener('seeked', handleSeeked)
    }
    props.init && props.init()
    return () => {
      if (internalRef.current !== null) {
        internalRef.current.removeEventListener('playing', handlePlaying)
        internalRef.current.removeEventListener('timeupdate', handleTimeUpdate)
        internalRef.current.removeEventListener('pause', handlePaused)
        internalRef.current.removeEventListener('error', handleError)
        internalRef.current.removeEventListener('ended', handleEnded)
        internalRef.current.removeEventListener('loadeddata', handleLoadedData)
        internalRef.current.removeEventListener('seeked', handleSeeked)
      }
      captionsManager.destroy()
    }
  }, [])

  return (
    <>
      <div
        id="video-player-captions-overlay"
        style={{
          visibility: props.captionsConfig?.enabled ? 'visible' : 'hidden',
        }}
      >
        {props.captionsConfig?.enabled && props.captionsAvailable && (
          <Captions captions={captions} captionStyles={props.captionsConfig} />
        )}
      </div>
      <video
        ref={(element) => {
          internalRef.current = element
          if (typeof playerRef === 'function') {
            playerRef(element)
          } else if (playerRef !== null) {
            playerRef.current = element
          }
        }}
        id={props.domID}
        data-testid="video"
      ></video>
    </>
  )
}

// @TODO: Disconnect from store and use props instead. This component is used both in GA and KIDs and both have different store structures.
const mapStateToProps = (state: any) => {
  return {
    captionsConfig: state.captions,
  }
}

export default connect(mapStateToProps, null, null, {
  forwardRef: true,
})(React.forwardRef(HTML5Player))
